<script setup>
definePage({
  name: 'shipment-warehouse-arrange-parcels',
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Parcels',
  },
})
import { ref, watch, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()

const containerId = computed(() => route.params.containerId)
const container = ref(null)
const groupedParcels = ref([])
const isLoading = ref(false)
const selectedClientGroups = ref([])
const isSubmitting = ref(false)

// Search and pagination
const searchQuery = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))

// Computed property to check if all displayed groups are selected
const areAllDisplayedSelected = computed(() => {
  if (!groupedParcels.value || groupedParcels.value.length === 0) {
    return false
  }
  
  return groupedParcels.value.every(group =>
    selectedClientGroups.value.some(selected => selected.customer_id === group.customer_id)
  )
})

// Fetch container details
const fetchContainer = async () => {
  try {
    const response = await axios.get(`shipment/container-arrivals/${containerId.value}`)

    container.value = response.data.data
  } catch (error) {
    console.error('Error fetching container:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.something_went_wrong'),
    })
  }
}

// Watch for changes in search or pagination
watch([searchQuery, currentPage, itemsPerPage], () => {
  fetchParcelsForArrangement()
})

// Handle page change
const handlePageChange = (page) => {
  currentPage.value = page
}

// Handle items per page change
const handleItemsPerPageChange = (perPage) => {
  itemsPerPage.value = perPage
  currentPage.value = 1 // Reset to first page when changing items per page
}

// Fetch parcels for arrangement
const fetchParcelsForArrangement = async () => {
  isLoading.value = true
  try {
    // Include search and pagination parameters
    const params = {
      container_id: containerId.value,
      page: currentPage.value,
      per_page: itemsPerPage.value
    }
    
    // Only add search if there's a value
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    
    const response = await axios.get('shipment/warehouse/parcels-for-arrangement', { params })
    
    groupedParcels.value = response.data.data
    
    // Update pagination info
    if (response.data.meta) {
      totalItems.value = response.data.meta.total
      currentPage.value = response.data.meta.current_page
    } else {
      totalItems.value = groupedParcels.value.length
    }
  } catch (error) {
    console.error('Error fetching parcels:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isLoading.value = false
  }
}

// Toggle selection of a client group
const toggleClientGroup = customerGroup => {
  const index = selectedClientGroups.value.findIndex(group => group.customer_id === customerGroup.customer_id)
  
  if (index > -1) {
    selectedClientGroups.value.splice(index, 1)
  } else {
    selectedClientGroups.value.push({
      customer_id: customerGroup.customer_id,
      parcel_ids: customerGroup.parcels.map(parcel => parcel.id),
    })
  }
}

// Check if a client group is selected
const isClientGroupSelected = customerGroup => {
  return selectedClientGroups.value.some(group => group.customer_id === customerGroup.customer_id)
}

// Submit selected client groups for processing
const createClientGroups = async () => {
  if (selectedClientGroups.value.length === 0) {
    showSnackbar({
      color: 'warning',
      text: t('shipment.select_client'),
    })
    
    return
  }
  
  isSubmitting.value = true
  try {
    const response = await axios.post('shipment/warehouse/create-client-groups', {
      container_id: containerId.value,
      groups: selectedClientGroups.value,
    })
    
    showSnackbar({
      color: 'success',
      text: t('shipment.client_groups_created'),
    })
    
    // Navigate to client groups view
    router.push({
      name: 'shipment-warehouse-client-groups',
      params: { containerId: containerId.value },
    })
  } catch (error) {
    console.error('Error creating client groups:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isSubmitting.value = false
  }
}

// One-click: fetch ALL customer groups across pages and create them.
// Each customer in the container becomes its own ClientArrivalGroup with all their parcels.
const createAllGroups = async () => {
  isSubmitting.value = true
  try {
    // Pull every customer-group on the server. The API caps per_page at 100,
    // so paginate explicitly until we've collected all of them.
    const allGroups = []
    let page = 1
    const perPage = 100
    while (true) {
      const resp = await axios.get('shipment/warehouse/parcels-for-arrangement', {
        params: { container_id: containerId.value, page, per_page: perPage },
      })
      const batch = resp.data.data || []
      allGroups.push(...batch)
      const meta = resp.data.meta || {}
      if (!meta.last_page || page >= meta.last_page || batch.length === 0) break
      page++
    }

    if (allGroups.length === 0) {
      showSnackbar({ color: 'warning', text: t('shipment.no_parcels_to_arrange') })
      return
    }

    const groups = allGroups.map(g => ({
      customer_id: g.customer_id,
      parcel_ids: g.parcels.map(p => p.id),
    }))

    await axios.post('shipment/warehouse/create-client-groups', {
      container_id: containerId.value,
      groups,
    })

    showSnackbar({
      color: 'success',
      text: `${groups.length} ${t('shipment.client_groups_created')}`,
    })

    router.push({
      name: 'shipment-warehouse-client-groups',
      params: { containerId: containerId.value },
    })
  } catch (error) {
    console.error('Error creating all client groups:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isSubmitting.value = false
  }
}

// Toggle select all displayed groups
const toggleSelectAll = () => {
  if (areAllDisplayedSelected.value) {
    // Deselect all currently displayed
    const displayedCustomerIds = groupedParcels.value.map(g => g.customer_id)
    
    selectedClientGroups.value = selectedClientGroups.value.filter(
      selected => !displayedCustomerIds.includes(selected.customer_id)
    )
  } else {
    // Select all currently displayed that aren't already selected
    groupedParcels.value.forEach(customerGroup => {
      if (!isClientGroupSelected(customerGroup)) {
        selectedClientGroups.value.push({
          customer_id: customerGroup.customer_id,
          parcel_ids: customerGroup.parcels.map(parcel => parcel.id),
        })
      }
    })
  }
}

// Navigate back to warehouse operations
const goBack = () => {
  router.push({ name: 'shipment-warehouse' })
}

onMounted(() => {
  fetchContainer()
  fetchParcelsForArrangement()
})
</script>

<template>
  <div style="font-family: 'Inter', 'Segoe UI', sans-serif;">
    <VCard class="mb-4">
      <VCardText>
        <VRow>
          <VCol cols="12">
            <div class="d-flex align-center justify-space-between">
              <h2 class="text-h4">
                {{ t('shipment.arrange_parcels') }}
              </h2>
              <VBtn icon variant="text" @click="goBack">
                <VIcon icon="tabler-arrow-left" />
              </VBtn>
            </div>
          </VCol>
          <VCol v-if="container" cols="12">
            <VChip color="primary" variant="elevated" size="large" class="me-2" prepend-icon="tabler-container">
              {{ container.reference_code || `#${container.id}` }}
            </VChip>
            <VChip color="info" variant="elevated" size="large" class="me-2" :prepend-icon="container.transport_type === 'air' ? 'tabler-plane' : 'tabler-ship'">
              {{ container.transport_type === 'air' ? t('shipment.air') : t('shipment.sea') }}
            </VChip>
            <VChip color="success" variant="elevated" size="large" prepend-icon="tabler-clock">
              {{ container.departure_date ? new Date(container.departure_date).toLocaleDateString() : t('shipment.no_date') }}
            </VChip>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VCard>
      <VCardText>
        <VRow>
          <VCol cols="12" md="8">
            <h3 class="text-h5">
              {{ t('shipment.parcels_for_arrangement') }}
            </h3>
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="searchQuery"
              :label="t('general.search')"
              :placeholder="t('shipment.search_customer_parcel_placeholder')"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="tabler-search"
              @update:model-value="currentPage = 1"
            />
          </VCol>
        </VRow>

        <VRow class="mb-4">
          <VCol cols="12" md="8">
            <div class="text-caption text-medium-emphasis">
              {{ t('general.showing') }} {{ groupedParcels.length ? (currentPage - 1) * itemsPerPage + 1 : 0 }}
              {{ t('general.to') }}
              {{ Math.min(currentPage * itemsPerPage, totalItems) }}
              {{ t('general.of') }} {{ totalItems }} {{ t('general.entries') }}
            </div>
          </VCol>
          <VCol cols="12" md="4" class="d-flex justify-end align-center flex-wrap ga-2">
            <VBtn
              v-if="totalItems > 0"
              color="success"
              size="large"
              class="rounded-pill px-6"
              elevation="2"
              :loading="isSubmitting"
              @click="createAllGroups"
            >
              <VIcon start icon="tabler-bolt" />
              {{ t('shipment.create_all_groups') || 'Create All Groups' }} ({{ totalItems }})
            </VBtn>
            <VBtn
              v-if="groupedParcels.length > 0"
              :color="areAllDisplayedSelected ? 'warning' : 'secondary'"
              variant="tonal"
              size="large"
              class="rounded-pill px-6"
              elevation="1"
              @click="toggleSelectAll"
            >
              <VIcon start :icon="areAllDisplayedSelected ? 'tabler-checkbox-x' : 'tabler-checkbox'" />
              {{ areAllDisplayedSelected ? t('general.deselect_all') : t('general.select_all') }}
            </VBtn>
            <VBtn
              color="primary"
              size="large"
              class="rounded-pill px-6"
              elevation="2"
              :loading="isSubmitting"
              :disabled="selectedClientGroups.length === 0"
              @click="createClientGroups"
            >
              <VIcon start icon="tabler-users-group" />
              {{ t('shipment.create_client_groups') }}
            </VBtn>
          </VCol>
        </VRow>

        <VProgressLinear v-if="isLoading" indeterminate />

        <VAlert
          v-else-if="groupedParcels.length === 0"
          type="info"
          variant="tonal"
          class="my-4"
          prepend-icon="tabler-info-circle"
        >
          <div class="text-body-1 font-weight-medium mb-1">
            {{ t('shipment.arrange_parcels_empty_title') }}
          </div>
          <div class="text-body-2 mb-3">
            {{ t('shipment.arrange_parcels_empty_hint') }}
          </div>
          <VBtn
            color="primary"
            size="small"
            variant="tonal"
            prepend-icon="tabler-truck-delivery"
            :to="`/shipment/container-arrivals/${containerId}/edit`"
          >
            {{ t('shipment.arrange_parcels_empty_action') }}
          </VBtn>
        </VAlert>

        <div v-else>
          <VCard
            v-for="customerGroup in groupedParcels"
            :key="customerGroup.customer_id"
            variant="outlined"
            class="mb-4 client-group-card"
            :class="{ 'selected': isClientGroupSelected(customerGroup) }"
            @click="toggleClientGroup(customerGroup)"
          >
            <VCardText>
              <VRow>
                <VCol cols="12" md="6">
                  <div class="d-flex align-center">
                    <VCheckbox
                      :model-value="isClientGroupSelected(customerGroup)"
                      hide-details
                      class="me-2 checkbox-effect"
                    />
                    <div>
                      <h3 class="text-h6 mb-1">
                        {{ customerGroup.customer_name }}
                      </h3>
                      <p class="text-subtitle-2 mb-0">
                        {{ customerGroup.customer_phone }}
                      </p>
                    </div>
                  </div>
                </VCol>
                <VCol cols="12" md="6">
                  <div class="d-flex justify-end">
                    <VChip color="primary" variant="tonal" class="me-2" prepend-icon="tabler-package">
                      {{ t('shipment.parcel_count') }}: {{ customerGroup.parcel_count }}
                    </VChip>
                    <VChip color="info" variant="tonal" class="me-2" prepend-icon="tabler-weight">
                      {{ customerGroup.total_weight }} {{ t('shipment.weight_unit') }}
                    </VChip>
                    <VChip color="success" variant="tonal" prepend-icon="tabler-cube">
                      {{ customerGroup.total_volume.toFixed(4) }} m³
                    </VChip>
                  </div>
                </VCol>
              </VRow>

              <VExpansionPanels variant="accordion" class="mt-4">
                <VExpansionPanel>
                  <VExpansionPanelTitle>
                    {{ t('shipment.parcels') }} ({{ customerGroup.parcel_count }})
                  </VExpansionPanelTitle>
                  <VExpansionPanelText>
                    <VTable class="parcel-table">
                      <thead>
                      <tr>
                        <th>{{ t('shipment.barcode') }}</th>
                        <th>{{ t('shipment.dimension') }}</th>
                        <th>{{ t('shipment.weight') }}</th>
                        <th>{{ t('shipment.volume') }}</th>
                        <th>{{ t('shipment.product') }}</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="parcel in customerGroup.parcels" :key="parcel.id">
                        <td>{{ parcel.barcode }}</td>
                        <td>{{ parcel.length }} × {{ parcel.width }} × {{ parcel.height }} cm</td>
                        <td>{{ parcel.weight }} {{ t('shipment.weight_unit') }}</td>
                        <td>{{ parcel.volume.toFixed(4) }} m³</td>
                        <td>{{ parcel.product_text || '-' }}</td>
                      </tr>
                      </tbody>
                    </VTable>
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>
            </VCardText>
          </VCard>

          <div class="mt-6">
            <VRow>
              <VCol cols="12" md="8" class="d-flex justify-center align-center">
                <VPagination
                  v-model="currentPage"
                  :length="totalPages"
                  :total-visible="5"
                  density="comfortable"
                  rounded
                  @update:model-value="handlePageChange"
                />
              </VCol>
              <VCol cols="12" md="4" class="d-flex justify-end align-center">
                <VSelect
                  v-model="itemsPerPage"
                  :items="[5, 10, 15, 25, 50]"
                  :label="t('general.items_per_page')"
                  density="compact"
                  variant="outlined"
                  hide-details
                  class="items-per-page-select"
                  style="max-width: 150px"
                  @update:model-value="handleItemsPerPageChange"
                />
              </VCol>
            </VRow>
          </div>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.client-group-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.client-group-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.4);
  background-color: rgba(var(--v-theme-primary), 0.03);
}

.client-group-card.selected {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.parcel-table {
  font-size: 0.875rem;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 6px;
}

.parcel-table tr {
  background: rgba(var(--v-theme-surface-variant), 0.5);
  border-radius: 8px;
}

.parcel-table th {
  text-align: left;
  color: rgba(var(--v-theme-on-surface), 0.6);
  padding: 8px 12px;
}

.parcel-table td {
  padding: 10px 12px;
  color: rgba(var(--v-theme-on-surface), 0.9);
}

.checkbox-effect {
  transition: transform 0.1s ease;
}

.checkbox-effect:hover {
  transform: scale(1.05);
}
</style>
